import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import BASE_URL from "../config";
import Sortable from "sortablejs";
import { toast } from "react-toastify";
import Loader from "../components/Loader"; // Import the Loader component

const parseDate = (dateString) => {
  if (dateString.includes("/")) {
    // Handle DD/MM/YYYY format
    const [day, month, year] = dateString.split("/").map(Number);
    return new Date(year, month - 1, day); // JavaScript months are 0-based
  } else if (dateString.includes("-")) {
    // Handle YYYY-MM-DD HH:mm:ss format
    return new Date(dateString);
  }
  return null; // Return null if the format is unrecognized
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [activeClients, setActiveClients] = useState([]);
  const [activeBills, setActiveBills] = useState([]);
  const [overdueClients, setOverdueClients] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [billsPaymentsDue, setBillsPaymentsDue] = useState([]);
  const [pastClients, setPastClients] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [overdueBills, setOverdueBills] = useState([]); // State for overdue bills
  const [unPaidBills, setUnPaidBills] = useState([]); // State for overdue bills
  const [billsUnpaid, setBillsUnpaid] = useState([]); // State for unpaid bills
  const [clientsUnpaid, setClientsUnpaid] = useState([]); // State for unpaid clients

  useEffect(() => {
    const unauthenticated = localStorage.getItem("token") === null || localStorage.getItem("token") === "";
    if (unauthenticated) {
      window.location.href = "/login";
      return;
    }
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${BASE_URL}api/clients/with-payments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        setClients(data);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch clients");
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${BASE_URL}api/clients/with-payments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        // Filter out records with active: 0 and format next_due_date
        const filteredData = data
          .filter(client => client.active !== 0)
          .map(client => ({
            name: client.name,
            email: client.email,
            date: client.payments?.[0]?.next_due_date
              ? new Date(client.payments[0].next_due_date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "N/A",
          }));

        console.log("Filtered and Formatted Data:", filteredData); // Log the filtered and formatted data

        // Separate activeBills and activeClients
        const bills = filteredData.filter(client => client.email === "bill@softisans.com" || client.email === "bills@softisans.com");
        const clients = filteredData.filter(client => client.email !== "bill@softisans.com" && client.email !== "bills@softisans.com");

        setActiveBills(bills);
        setActiveClients(clients);

        // Set pastClients where active is 0
        const pastClientsData = data.filter(client => client.active === 0).map(client => ({
          name: client.name,
          email: client.email,
          date: client.payments?.[0]?.next_due_date
            ? new Date(client.payments[0].next_due_date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "N/A",
        }));

        setPastClients(pastClientsData);

        console.log("Active Bills:", bills);
        console.log("Active Clients:", clients);
        console.log("Past Clients:", pastClientsData); // Log past clients
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchUnpaidClients = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${BASE_URL}api/clients/with-unpaid-payments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        console.log("Unpaid Clients Data:", data); // Log the unpaid clients

        const now = new Date();
        now.setHours(0, 0, 0, 0); // Normalize today's date to midnight

        // Filter overdue bills
        const overdueBills = data.filter(client =>
          (client.email === "bills@softisans.com" || client.email === "bill@softisans.com") &&
          client.payments.some(payment => {
            const nextDueDate = parseDate(payment.next_due_date);
            return nextDueDate < now; // Include payments with due dates before today
          })
        );

        // Filter overdue clients
        const overdueClients = data.filter(client =>
          (client.email !== "bills@softisans.com" && client.email !== "bill@softisans.com") &&
          client.payments.some(payment => {
            const nextDueDate = parseDate(payment.next_due_date);
            return nextDueDate < now; // Include payments with due dates before today
          })
        );

        setOverdueBills(
          overdueBills.map(client => ({
            ...client,
            days: `${Math.ceil((now - parseDate(client.payments[0].next_due_date)) / (1000 * 60 * 60 * 24))} days overdue`
          }))
        ); // Set overdue bills

        setOverdueClients(
          overdueClients.map(client => ({
            ...client,
            days: `${Math.ceil((now - parseDate(client.payments[0].next_due_date)) / (1000 * 60 * 60 * 24))} days overdue`
          }))
        ); // Set overdue clients

        console.log("Filtered Overdue Bills:", overdueBills); // Log overdue bills
        console.log("Filtered Overdue Clients:", overdueClients); // Log overdue clients

        const nextWeek = new Date();
        nextWeek.setDate(now.getDate() + 7); // Calculate the date 7 days from today

        // Filter records into separate states
        const bills = data.filter(client => 
          (client.email === "bills@softisans.com" || client.email === "bill@softisans.com") &&
          client.payments.some(payment => {
            const nextDueDate = parseDate(payment.next_due_date);
            return nextDueDate >= now && nextDueDate <= nextWeek; // Include payments with due dates between today and (today + 7)
          })
        );

        const clients = data.filter(client => 
          (client.email !== "bills@softisans.com" && client.email !== "bill@softisans.com") &&
          client.payments.some(payment => {
            const nextDueDate = parseDate(payment.next_due_date);
            return nextDueDate >= now && nextDueDate <= nextWeek; // Include payments with due dates between today and (today + 7)
          })
        );

        setBillsUnpaid(bills); // Set unpaid bills
        setClientsUnpaid(clients); // Set unpaid clients
        setBillsPaymentsDue(
          bills.map(client => ({
            ...client, // Copies all existing properties of the client object
            date: parseDate(client.payments[0].next_due_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) // Adds or overrides the date property
          }))
        );
        setUpcomingPayments(
          clients.map(client => ({
            ...client, // Copies all existing properties of the client object
            date: parseDate(client.payments[0].next_due_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) // Adds or overrides the date property
          }))
        );
        console.log("Filtered Unpaid Bills (Due Today to Next 7 Days):", bills); // Log filtered unpaid bills
        console.log("Filtered Unpaid Clients (Due Today to Next 7 Days):", clientsUnpaid); // Log filtered unpaid clients
      } catch (error) {
        console.error("Error fetching unpaid clients:", error);
      }
    };

    fetchUnpaidClients();
  }, []);

  useEffect(() => {
    document.querySelectorAll(".task-list").forEach((el) => {
      new Sortable(el, {
        group: "kanban",
        animation: 150,
        fallbackOnBody: true,
        swapThreshold: 0.65,
        onEnd: async (evt) => {
          const taskId = evt.item.dataset.id; // Get the task ID from the dragged element
          const newList = evt.to.dataset.list; // Get the new list name
          const oldList = evt.from.dataset.list; // Get the old list name

          if (taskId && newList && oldList) {
            try {
              // Update the task in the backend
              const response = await fetch(`${BASE_URL}api/tasks/update/${taskId}`, {
                method: "PUT",
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ list: newList }),
              });

              if (response.ok) {
                // Update the task in the frontend state
                setTaskData((prev) =>
                  prev.map((task) =>
                    task.id === parseInt(taskId) ? { ...task, list: newList } : task
                  )
                );

                // Remove the task from the old list in the DOM
                evt.from.removeChild(evt.item);

                toast.success("Task updated successfully!"); // Show success notification
              } else {
                throw new Error("Failed to update task");
              }
            } catch (err) {
              console.error("Error updating task:", err);
              toast.error("Error updating task."); // Show error notification
            }
          }
        },
      });
    });
  }, [taskData]);

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.email.toLowerCase().includes(search.toLowerCase())
  );

  const sortedClients = [...filteredClients].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valA = sortConfig.key === "next_due_date" && a.payments?.length > 0
      ? new Date(a.payments[0].next_due_date).getTime()
      : a[sortConfig.key]?.toString().toLowerCase() || "";
    const valB = sortConfig.key === "next_due_date" && b.payments?.length > 0
      ? new Date(b.payments[0].next_due_date).getTime()
      : b[sortConfig.key]?.toString().toLowerCase() || "";
    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedClients.length / pageSize);
  const paginatedClients = sortedClients.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const toggleClientStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${newStatus === 1 ? "activate" : "deactivate"} this client?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.put(`${BASE_URL}api/clients/update/${id}`,
        { active: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setClients((prevClients) =>
        prevClients.map((client) =>
          client.id === id ? { ...client, active: newStatus } : client
        )
      );
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update client status");
    }
  };


  return (
    <div className="container mt-4" style={{ fontSize: "0.9em" }}>
      {loading ? (
        <Loader message="Loading clients..." /> 
      ) : (
        <>
          <div className="mb-3 d-flex justify-content-between">
            <h2 style={{ fontSize: "1.5em" }}>Clients and Bills</h2>
            <a href="add-client" className="btn btn-primary d-flex align-items-center" style={{ fontSize: "0.9em" }}>Add Client</a>
          </div>
          <div className="row">
            {activeClients.length > 0 && <ClientCard title="Active Clients" bgColor="bg-success" clients={activeClients} />}
            {pastClients.length > 0 && <ClientCard title="Past Clients" bgColor="bg-secondary" clients={pastClients} />}
            {/* {overdueClients.length > 0 && <ClientCard title="Overdue Clients" bgColor="bg-danger" clients={overdueClients} />} */}
            {/* {upcomingPayments.length > 0 && <ClientCard title="Payments Due" bgColor="bg-warning" clients={upcomingPayments} />} */}
            {activeBills.length > 0 && <ClientCard title="Active Bills" bgColor="bg-info" clients={activeBills} />}
            {billsPaymentsDue.length > 0 && <ClientCard title="Bills Payments Due" bgColor="bg-primary" clients={billsPaymentsDue} />}
            {/* {overdueBills.length > 0 && <ClientCard title="Overdue Bills" bgColor="bg-danger" clients={overdueBills} />} */}
          </div>

          <div className="row mb-3">
            <div className="col-md-2">
              <select
                className="form-select"
                style={{ fontSize: "0.9em" }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value="5">Show 5 Clients</option>
                <option value="10">Show 10 Clients</option>
                <option value="20">Show 20 Clients</option>
                <option value="50">Show 50 Clients</option>
              </select>
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                style={{ fontSize: "0.9em" }}
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <table className="table table-striped" style={{ fontSize: "0.9em" }}>
            <thead className="table-dark">
              <tr>

                <th onClick={() => requestSort("name")} style={{ cursor: "pointer" }}>Name</th>
                <th>Contact</th>
                <th onClick={() => requestSort("email")} style={{ cursor: "pointer" }}>Email</th>
                <th onClick={() => requestSort("payment_type")} style={{ cursor: "pointer" }}>Payment Type</th>
                <th onClick={() => requestSort("last_payment_date")} style={{ cursor: "pointer" }}>Last Payment</th>
                <th onClick={() => requestSort("next_due_date")} style={{ cursor: "pointer" }}>Next Due</th>
                <th onClick={() => requestSort("active")} style={{ cursor: "pointer" }}>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedClients.length > 0 ? (
                paginatedClients.map((client) => {
                  const latestPayment = client.payments?.reduce((latest, payment) =>
                    new Date(payment.last_payment_date) > new Date(latest.last_payment_date) ? payment : latest,
                    client.payments[0]
                  );

                  return (
                    <tr key={client.id}>
                      <td><a href={`client-detail?id=${client.id}`} style={{ fontSize: "0.9em" }}>{client.name}</a></td>
                      <td>
                        {client.contact_type === "Whatsapp" && client.phone ? (
                          <a href={`https://wa.me/${client.phone}`} target="_blank" rel="noopener noreferrer" className="btn btn-success btn-sm" style={{ fontSize: "0.8em" }}>
                            WhatsApp
                          </a>
                        ) : client.contact_type === "Skype" && client.phone ? (
                          <a href={`skype:${client.phone}?chat`} className="btn btn-primary btn-sm" style={{ fontSize: "0.8em" }}>Skype</a>
                        ) : (
                          <span style={{ fontSize: "0.9em" }}>{client.phone || "N/A"}</span>
                        )}
                      </td>
                      <td><a href={`mailto:${client.email}`} style={{ fontSize: "0.9em" }}>{client.email}</a></td>
                      <td>{client.payment_type}</td>
                      <td>
                        {latestPayment
                          ? new Date(latestPayment.last_payment_date).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                          : "N/A"}
                      </td>
                      <td
                        className={
                          latestPayment &&
                            client.active === 1 &&
                            (() => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const dueDate = new Date(latestPayment.next_due_date);
                              dueDate.setHours(0, 0, 0, 0);
                              const nextWeek = new Date();
                              nextWeek.setDate(nextWeek.getDate() + 7);
                              nextWeek.setHours(0, 0, 0, 0);
                              return dueDate <= nextWeek && dueDate >= today;
                            })()
                            ? "text-danger"
                            : ""
                        }
                      >
                        {client.active === 1 && latestPayment
                          ? new Date(latestPayment.next_due_date).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                          : ""}
                      </td>




                      <td>
                        <button
                          className={`btn btn-sm ${client.active === 1 ? "btn-success" : "btn-danger"}`}
                          style={{ fontSize: "0.8em" }}
                          onClick={() => toggleClientStatus(client.id, client.active)}
                        >
                          {client.active === 1 ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td>
                        <a href={`edit-client?id=${client.id}`} className="btn btn-sm btn-warning mx-2" style={{ fontSize: "0.8em" }}>Edit</a>
                        <a href={`client-payments?id=${client.id}`} className="btn btn-sm btn-info" style={{ fontSize: "0.8em" }}>Payments</a>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center text-muted" style={{ fontSize: "0.9em" }}>No clients found</td>
                </tr>
              )}
            </tbody>
          </table>
          {/* Pagination */}
          <div className="d-flex justify-content-between" style={{ fontSize: "0.9em" }}>
            <span>Showing {paginatedClients.length} of {filteredClients.length} clients</span>
            <div>
              <button
                className="btn btn-outline-primary mx-1"
                style={{ fontSize: "0.8em" }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <span className="mx-2">Page {currentPage} of {totalPages}</span>
              <button
                className="btn btn-outline-primary mx-1"
                style={{ fontSize: "0.8em" }}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
const ClientCard = ({ title, bgColor, clients }) => (
  <div className="col-md-3">
    <div className={`card text-white ${bgColor} mb-3`}>
      <div className="card-header">{title}</div>
      <div className="card-body">
        <ul className="client-list list-unstyled">
          {clients.length > 0 ? (
            clients.map((client, index) => (
              <li key={index} className="list-group-item text-start">
                <span>
                  <span>{client.name}</span>{" "}
                  <span className="small-muted" style={{ fontSize: "0.8em", color: "rgba(255, 255, 255, 0.7)" }}>
                    {client.date && !isNaN(new Date(client.date).getTime())
                      ? new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(new Date(client.date))
                      : client.days }
                  </span>
                </span>
              </li>
            ))
          ) : (
            <li className="list-group-item text-white text-start">No clients found</li>
          )}
        </ul>
      </div>
    </div>
  </div>
);

export default Clients;
